import { useEffect } from "react";
import {
  useCountrySelections,
  useCouponPromotionCodes,
  useCtxReferences,
  useShippingRates,
  useTaxRates
} from "utils/selectors";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import cloneDeep from "lodash/cloneDeep";
import {
  getShortLinkCheckoutUrlVariations,
  getShortLinkMode,
  invalidShortLinkPaymentMethodForFutureUsage,
  shortLinkHasRecurringPrices
} from "utils/shortLink";
import { ORGANIZATION } from "utils/constants/models";
import { FIRST_PHASE_ITERATIONS_PATH, STATE_KEYS } from "utils/constants/state";
import { useFormikContext } from "formik";
import { reduceIdMap } from "utils/data";
import {
  AUTHORIZE_PAYMENT_COPY,
  CAPTURE_METHOD,
  PAYMENT_MODE,
  SETUP_FUTURE_USAGE_COPY
} from "utils/constants/payment";
import { getFieldPath } from "utils/form";
import { prepareShippingRateShow } from "utils/shippingRate";
import { getMessagesError } from "./editor";
import { prepareTaxRateShow } from "utils/taxRate";
import { TAX_ID_COLLECTION_TYPE } from "utils/constants/taxRate";
import { SHORT_LINK_FIELDS } from "utils/constants/shortLink";
import { getShowConfigTaxFields } from "utils/configFields/tax";

export const useShortLinkCheckoutUrl = ({ test, shortId }) => {
  const ctxReferences = useCtxReferences();
  if (
    ctxReferences &&
    ctxReferences.application &&
    ctxReferences.resource &&
    shortId
  ) {
    const { resource, application } = ctxReferences;
    const isOrg = resource === ORGANIZATION;
    const variations = getShortLinkCheckoutUrlVariations({
      alias: get(application, "alias"),
      subdomain: get(application, "subdomain"),
      shortId: shortId
    });
    const subdomainOriginalUrl = variations.subdomain.original;
    const subdomainTestUrl = variations.subdomain.test;

    const aliasOriginalUrl = variations.alias.original;
    const aliasTestUrl = variations.alias.test;

    const standardOriginalUrl = variations.standard.original;
    const standardTestUrl = variations.standard.test;

    if (isOrg) {
      return test ? subdomainTestUrl : subdomainOriginalUrl;
    } else if (application.alias) {
      return test ? aliasTestUrl : aliasOriginalUrl;
    } else {
      return test ? standardTestUrl : standardOriginalUrl;
    }
  }
};

export const useShortLinkSubscriptionSchedule = (props) => {
  const { values, errors } = useFormikContext();
  const inherited = props && props.inherited;

  const error = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE
  );
  /**
   * =============
   * Values
   * =============
   */
  const subscriptionSchedule = get(
    values,
    STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE
  );

  const iterationsPath = `${STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE}.${FIRST_PHASE_ITERATIONS_PATH}`;
  const subscriptionScheduleIterations = get(values, iterationsPath);
  const hasSchedule = !isEmpty(subscriptionSchedule);
  const primaries = get(values, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
  const emptyState = primaries.length === 0;
  const hasRecurring = shortLinkHasRecurringPrices(values);
  const hasDefinedSchedule =
    typeof subscriptionScheduleIterations !== "undefined";

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedRecurring = shortLinkHasRecurringPrices(inherited);
  const inheritedSubscriptionSchedule = get(
    inherited,
    STATE_KEYS.SHORT_LINK.SUBSCRIPTION_SCHEDULE
  );
  const hasDefinedInheritedSchedule =
    typeof inheritedSubscriptionSchedule !== "undefined";

  const activeInheritence = !hasDefinedSchedule && hasDefinedInheritedSchedule;
  /**
   * Disable when neither value sets have recurring prices
   * - current
   * - inherited
   * But allow to enable when no primaries exists at all (i.e. common config)
   */
  const isSetupMode = getIsSetupMode({ values, inherited });
  let disabled = isSetupMode || (!hasRecurring && !inheritedRecurring);
  if (emptyState && disabled) {
    disabled = false;
  }

  return {
    error,
    hasSchedule,
    subscriptionSchedule,
    inheritedSubscriptionSchedule,
    disabled,
    activeInheritence
  };
};

const reduceDiscounts = ({ discounts, coupons, promotionCodes }) => {
  const result = [];
  if (Array.isArray(discounts) && discounts.length > 0) {
    for (let discIs = 0; discIs < discounts.length; discIs++) {
      const { coupon, promotion_code } = discounts[discIs];
      if (coupon && coupons[coupon]) {
        result.push(coupons[coupon]);
      } else if (
        promotion_code &&
        promotionCodes[promotion_code] &&
        promotionCodes[promotion_code].coupon
      ) {
        result.push({
          ...promotionCodes[promotion_code].coupon,
          promotion_code: omit(promotionCodes[promotion_code], "coupon")
        });
      }
    }
  }
  return result;
};

export const useShortLinkDiscounts = (props) => {
  const { values, errors } = useFormikContext();
  const inherited = props && props.inherited;
  const discountsError = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.DISCOUNTS
  );
  const allowPromotionCodesError = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES
  );

  const { coupons, promotionCodes } = useCouponPromotionCodes();
  const couponMap = reduceIdMap(coupons);
  const promotionCodeMap = reduceIdMap(promotionCodes);
  const sharedInput = {
    coupons: couponMap,
    promotionCodes: promotionCodeMap
  };

  /**
   * =============
   * Values
   * =============
   */
  const discounts = get(values, STATE_KEYS.SHORT_LINK.DISCOUNTS);
  const hasDiscounts = Array.isArray(discounts) && discounts.length > 0;
  const ctxDiscounts = reduceDiscounts({
    discounts,
    ...sharedInput
  });
  const allowPromotionCodes = get(
    values,
    STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES
  );
  const hasDefinedDiscounts = typeof discounts !== "undefined";
  const hasDefinedPromotionCodes = typeof allowPromotionCodes !== "undefined";
  const anyDefinedOverrides = hasDefinedDiscounts || hasDefinedPromotionCodes;

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedDiscounts = get(inherited, STATE_KEYS.SHORT_LINK.DISCOUNTS);
  const ctxInheritedDiscounts = reduceDiscounts({
    discounts: inheritedDiscounts,
    ...sharedInput
  });
  const inheritedAllowPromotionCodes = get(
    inherited,
    STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES
  );
  const hasDefinedInheritedDiscounts =
    typeof inheritedDiscounts !== "undefined";
  const hasDefinedInheritedPromotionCodes =
    typeof inheritedAllowPromotionCodes !== "undefined";
  const anyDefinedInheritence =
    hasDefinedInheritedPromotionCodes || hasDefinedInheritedDiscounts;

  /**
   * Show active inheritance if:
   * - either of the inherited promo codes / discounts are defined
   */
  const activeInheritence = !anyDefinedOverrides && anyDefinedInheritence;

  return {
    discountsError,
    allowPromotionCodesError,
    allowPromotionCodes: Boolean(allowPromotionCodes),
    activeInheritence,
    inheritedDiscounts: ctxInheritedDiscounts,
    inheritedAllowPromotionCodes,
    hasDiscounts,
    discounts: ctxDiscounts,
    hasDefinedInheritedPromotionCodes,
    hasDefinedInheritedDiscounts
  };
};

export const useShortLinkPaymentMethods = (props) => {
  const { values, errors } = useFormikContext();
  const inherited = props && props.inherited;

  const error = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
  );

  /**
   * =============
   * Values
   * =============
   */
  const paymentMethodTypes = get(
    values,
    STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
  );
  const hasPaymentMethodTypes =
    Array.isArray(paymentMethodTypes) && paymentMethodTypes.length > 0;

  const hasDefinedPaymentMethodTypes =
    typeof paymentMethodTypes !== "undefined";
  const hasDefinedOverrides = hasDefinedPaymentMethodTypes;

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedPaymentMethodTypes = get(
    inherited,
    STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
  );
  const hasInheritedPaymentMethodTypes =
    Array.isArray(inheritedPaymentMethodTypes) &&
    inheritedPaymentMethodTypes.length > 0;
  const activeInheritence =
    !hasDefinedOverrides && hasInheritedPaymentMethodTypes;

  return {
    error,
    hasPaymentMethodTypes,
    paymentMethodTypes,
    inheritedPaymentMethodTypes,
    activeInheritence,
    hasDefinedPaymentMethodTypes
  };
};

export const useShortLinkTrial = (props) => {
  const { values, errors } = useFormikContext();
  const inherited = props && props.inherited;

  const error = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.TRIAL_PERIOD_DAYS
  );

  /**
   * =============
   * Values
   * =============
   */
  const trialPeriodDays = get(values, STATE_KEYS.SHORT_LINK.TRIAL_PERIOD_DAYS);

  const hasTrialPeriodDays =
    Number.isInteger(trialPeriodDays) && trialPeriodDays > 0;
  const hasDefinedTrialPeriodDays = typeof trialPeriodDays !== "undefined";
  const hasDefinedOverrides = hasDefinedTrialPeriodDays;

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedTrialPeriodDays = get(
    inherited,
    STATE_KEYS.SHORT_LINK.TRIAL_PERIOD_DAYS
  );
  const hasInheritedTrialPeriodDays =
    Number.isInteger(inheritedTrialPeriodDays) && inheritedTrialPeriodDays > 0;
  const activeInheritence = !hasDefinedOverrides && hasInheritedTrialPeriodDays;

  return {
    error,
    hasTrialPeriodDays,
    trialPeriodDays,
    inheritedTrialPeriodDays,
    activeInheritence,
    hasDefinedTrialPeriodDays
  };
};

export const useShortLinkSetupFutureUsage = (props) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const inherited = props && props.inherited;

  const error = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
  );
  /**
   * =============
   * Values
   * =============
   */
  const setupFutureUsage = get(
    values,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
  );
  const hasSetupFutureUsage = Boolean(setupFutureUsage);
  const hasDefinedSetupFutureUsage = typeof setupFutureUsage !== "undefined";
  const hasDefinedOverrides = hasDefinedSetupFutureUsage;

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedSetupFutureUsage = get(
    inherited,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
  );
  const hasInheritedSetupFutureUsage = Boolean(inheritedSetupFutureUsage);
  const activeInheritence =
    !hasDefinedOverrides && hasInheritedSetupFutureUsage;

  const invalidPaymentMethod = invalidShortLinkPaymentMethodForFutureUsage(
    values
  );
  const invalidInheritedPaymentMethod = invalidShortLinkPaymentMethodForFutureUsage(
    inherited
  );

  /**
   * Future usage only a suitable setting for payment mode
   * - Why? Cards will automatically be saved in subscription and setup mode
   */
  const mode = getShortLinkMode(values);
  const inheritedMode = getShortLinkMode(inherited);
  const invalidMode =
    mode !== PAYMENT_MODE.PAYMENT || inheritedMode !== PAYMENT_MODE.PAYMENT;
  const notice = invalidMode
    ? SETUP_FUTURE_USAGE_COPY.DISABLED_FOR_MODE
    : invalidPaymentMethod
    ? SETUP_FUTURE_USAGE_COPY.DISABLED_FOR_PAYMENT_METHOD
    : null;
  const disableSetupFutureUsage = invalidMode || invalidPaymentMethod;
  /**
   * Transition back to null when:
   * - mode is not payment and
   * - future usage is present
   */
  useEffect(() => {
    if (disableSetupFutureUsage && setupFutureUsage) {
      setTimeout(() => {
        setFieldValue(
          STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE,
          null
        );
      }, 0);
    }
  }, [mode]);

  return {
    error,
    hasSetupFutureUsage,
    setupFutureUsage,
    inheritedSetupFutureUsage,
    activeInheritence,
    hasDefinedSetupFutureUsage,
    invalidPaymentMethod,
    invalidInheritedPaymentMethod,
    disableSetupFutureUsage,
    invalidMode,
    notice
  };
};

export const useShortLinkMode = (props) => {
  const { values, errors } = useFormikContext();
  const inherited = props && props.inherited;

  const error = getMessagesError(errors, STATE_KEYS.SHORT_LINK.MODE);

  const primaries = get(values, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
  const hasPrimaries = primaries.length > 0;

  const mode = getShortLinkMode(values);
  const inheritedMode = getShortLinkMode(inherited);
  const hasDefinedMode = typeof mode !== "undefined";
  const hasDefinedOverrides = hasDefinedMode;
  const activeInheritence = !hasDefinedOverrides && inheritedMode;
  const notice = "Setup payment method only";
  const isSetupMode = mode === PAYMENT_MODE.SETUP;

  return {
    error,
    mode,
    isSetupMode,
    inheritedMode,
    activeInheritence,
    disabledMode: hasPrimaries,
    notice
  };
};

const getIsSetupMode = ({ values, inherited }) => {
  const mode = getShortLinkMode(values);
  const inheritedMode = getShortLinkMode(inherited);
  const ctxMode = inheritedMode || mode;
  return ctxMode === PAYMENT_MODE.SETUP;
};

export const useShortLinkCaptureMethod = (props) => {
  const { values, setFieldValue, errors } = useFormikContext();
  const inherited = props && props.inherited;
  const error = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD
  );

  /**
   * =============
   * Values
   * =============
   */
  const captureMethod = get(
    values,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD
  );
  const isManualCapture = captureMethod === CAPTURE_METHOD.MANUAL;
  const hasDefinedCaptureMethod = typeof captureMethod !== "undefined";
  const hasDefinedOverrides = hasDefinedCaptureMethod;

  /**
   * =============
   * Inheritence
   * =============
   */
  const inheritedCaptureMethod = get(
    inherited,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD
  );

  const inheritedIsManualCapture =
    inheritedCaptureMethod === CAPTURE_METHOD.MANUAL;
  const hasInheritedCaptureMethod = Boolean(inheritedCaptureMethod);
  const activeInheritence = !hasDefinedOverrides && hasInheritedCaptureMethod;

  const mode = getShortLinkMode(values);
  const inheritedMode = getShortLinkMode(inherited);
  const invalidMode =
    mode !== PAYMENT_MODE.PAYMENT || inheritedMode !== PAYMENT_MODE.PAYMENT;
  const disableManualCapture = invalidMode;

  const notice = disableManualCapture
    ? AUTHORIZE_PAYMENT_COPY.DISABLED
    : AUTHORIZE_PAYMENT_COPY.ENABLED;

  /**
   * Transition back to automatic capture method if mode is not payment
   */
  useEffect(() => {
    if (disableManualCapture && captureMethod) {
      setTimeout(() => {
        setFieldValue(
          STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD,
          null
        );
      }, 0);
    }
  }, [mode]);

  return {
    error,
    notice,
    isManualCapture,
    inheritedIsManualCapture,
    activeInheritence,
    disableManualCapture
  };
};

export const useShortLinkShipping = (props) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched
  } = useFormikContext();
  const inherited = props && props.inherited;
  const name = props && props.name;
  const shippingCountriesPath = getFieldPath(
    name,
    "shipping_address_collection.allowed_countries"
  );
  const shippingRates = useShippingRates();

  const shippingWorldwidePath = getFieldPath(name, "shipping_worldwide");
  const shippingOptionsPath = getFieldPath(name, "shipping_options");

  /**
   * =============
   * Values
   * =============
   */
  const countriesValue = get(values, shippingCountriesPath);
  const shippingOptionsValue = get(values, shippingOptionsPath);

  const {
    options: countryOptions,
    selections: countrySelections
  } = useCountrySelections(countriesValue);
  const shippingWorldwideActive = get(values, shippingWorldwidePath);
  const hasDefinedCountriesValue = typeof countriesValue !== "undefined";
  const hasDefinedShippingOptionsValue =
    typeof shippingOptionsValue !== "undefined";
  const hasDefinedShippingWorldwideValue =
    typeof shippingWorldwideActive !== "undefined";

  /**
   * =============
   * Inheritence
   * =============
   */
  const countriesInheritedValue = get(inherited, shippingCountriesPath);
  const shippingOptionsInheritedValue = get(inherited, shippingOptionsPath);
  const { selections: inheritedSelections } = useCountrySelections(
    countriesInheritedValue
  );
  const shippingWorldwideInheritedValue = get(inherited, shippingWorldwidePath);

  const hasDefinedCountriesInheritedValue =
    typeof countriesInheritedValue !== "undefined";
  const hasDefinedShippingWorldwideInheritedValue =
    typeof shippingWorldwideInheritedValue !== "undefined";
  const hasDefinedShippingRateOptionsInheritedValue =
    typeof shippingOptionsInheritedValue !== "undefined";

  const activeDestinationsInheritence =
    !hasDefinedCountriesValue &&
    !hasDefinedShippingWorldwideValue &&
    (hasDefinedCountriesInheritedValue ||
      hasDefinedShippingWorldwideInheritedValue);

  const activeShippingOptionsInheritence =
    !hasDefinedShippingOptionsValue &&
    hasDefinedShippingRateOptionsInheritedValue;

  /**
   * Shipping disabled
   * - Reset worldwide shipping to false
   * - Unset shipping countries
   * - Remove shipping rates
   * Shipping worldwide
   * - Unset shipping countries if:
   *  - shipping worldwide has been enabled
   *  - there are country selections chosen
   */
  useEffect(() => {
    if (shippingWorldwideActive && countrySelections.length) {
      if (!get(touched, shippingCountriesPath)) {
        setFieldTouched(shippingCountriesPath, true);
      }
      setTimeout(() => setFieldValue(shippingCountriesPath, []), 0);
    }
  }, [shippingWorldwideActive]);

  let preparedInheritedOptions = [];
  if (
    Array.isArray(shippingOptionsInheritedValue) &&
    Array.isArray(shippingRates) &&
    shippingRates.length &&
    shippingOptionsInheritedValue.length
  ) {
    preparedInheritedOptions = shippingRates
      .filter(({ id }) =>
        shippingOptionsInheritedValue.find(
          ({ shipping_rate }) => shipping_rate === id
        )
      )
      .map((shippingRate) => prepareShippingRateShow({ shippingRate }));
  }

  return {
    // Values
    countryOptions,
    countrySelections,
    shippingOptionsValue,
    shippingWorldwideActive,
    // -----
    activeDestinationsInheritence,
    activeShippingOptionsInheritence,
    // -----
    // Inheritence
    hasDefinedShippingWorldwideInheritedValue,
    hasDefinedCountriesInheritedValue,
    hasDefinedShippingRateOptionsInheritedValue,
    inheritedSelections,
    shippingOptionsInheritedValue,
    preparedInheritedOptions,
    shippingWorldwideInheritedValue
  };
};

export const useShortLinkTaxes = (props) => {
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    errors
  } = useFormikContext();
  const inherited = props && props.inherited;
  const fieldsPreTouched = get(touched, STATE_KEYS.SHORT_LINK.FIELDS_PRE);
  const taxRatesTouched = get(touched, STATE_KEYS.SHORT_LINK.TAX_RATES);

  const taxRatesModels = useTaxRates();
  const autoTaxError = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED
  );
  const taxRatesError = getMessagesError(
    errors,
    STATE_KEYS.SHORT_LINK.TAX_RATES
  );

  /**
   * Values
   */
  const standardAutoTaxes = get(
    values,
    STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED
  );
  const standardTaxRates = get(values, STATE_KEYS.SHORT_LINK.TAX_RATES);
  const taxId = get(values, STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_ENABLED);
  const taxIdType = get(values, STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_TYPE);

  /**
   * Inherited
   */
  const inheritedAutoTaxes = get(
    inherited,
    STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED
  );
  const inheritedTaxRates = get(inherited, STATE_KEYS.SHORT_LINK.TAX_RATES);
  const inheritedTaxIdType = get(
    inherited,
    STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_TYPE
  );
  const inheritedTaxIdTypeEnabled = get(
    inherited,
    STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_ENABLED
  );

  /**
   * Show logic
   */
  const {
    showStandardAutoTaxes,
    showInheritedAutoTaxes,
    showStandardTaxRates,
    showInheritedTaxRates,
    showInheritedTaxId
  } = getShowConfigTaxFields({ inherited, values });

  /**
   * Lifecycle
   */
  const fieldsPre = get(values, STATE_KEYS.SHORT_LINK.FIELDS_PRE, []);
  const tinFieldIndex = fieldsPre.findIndex(
    ({ uid }) => uid === SHORT_LINK_FIELDS.TIN
  );
  const tinFieldActive = tinFieldIndex > -1;
  const taxRateCount = Array.isArray(standardTaxRates)
    ? standardTaxRates.length
    : 0;

  useEffect(() => {
    if (standardAutoTaxes && taxRateCount) {
      setFieldValue(STATE_KEYS.SHORT_LINK.TAX_RATES, []);
      if (!taxRatesTouched) {
        setFieldTouched(STATE_KEYS.SHORT_LINK.TAX_RATES, true);
      }
    }
  }, [standardAutoTaxes, taxRateCount]);

  /**
   * Remove present field if tax id collection:
   * - disabled
   * - type is standard
   * Add field if tax id collection is:
   * - enabled
   * - field not present
   * - type is international
   */
  useEffect(() => {
    const removeTaxIdPreField =
      (!taxId && tinFieldActive) ||
      (taxIdType === TAX_ID_COLLECTION_TYPE.STANDARD && tinFieldActive);

    const addTaxIdPreField =
      taxId &&
      taxIdType === TAX_ID_COLLECTION_TYPE.INTERNATIONAL &&
      !tinFieldActive;

    const updatedFields = cloneDeep(fieldsPre);
    if (removeTaxIdPreField || !taxId) {
      updatedFields.splice(tinFieldIndex, 1);
      setFieldValue(STATE_KEYS.SHORT_LINK.FIELDS_PRE, updatedFields);
      if (!fieldsPreTouched) {
        setFieldTouched(STATE_KEYS.SHORT_LINK.FIELDS_PRE, true);
      }
    } else if (addTaxIdPreField) {
      updatedFields.push({
        uid: SHORT_LINK_FIELDS.TIN,
        required: true
      });
      setFieldValue(STATE_KEYS.SHORT_LINK.FIELDS_PRE, updatedFields);
      if (!fieldsPreTouched) {
        setFieldTouched(STATE_KEYS.SHORT_LINK.FIELDS_PRE, true);
      }
    }
  }, [taxId, taxIdType]);

  let preparedInheritedTaxRates = [];
  if (
    Array.isArray(inheritedTaxRates) &&
    Array.isArray(taxRatesModels) &&
    taxRatesModels.length &&
    inheritedTaxRates.length
  ) {
    preparedInheritedTaxRates = taxRatesModels
      .filter(({ id }) =>
        inheritedTaxRates.find((taxRateId) => taxRateId === id)
      )
      .map((taxRate) => prepareTaxRateShow({ taxRate }));
  }

  return {
    autoTaxError,
    taxRatesError,
    inheritedAutoTaxes,
    inheritedTaxIdType,
    inheritedTaxIdTypeEnabled,
    preparedInheritedTaxRates,
    showInheritedAutoTaxes,
    showStandardAutoTaxes,
    showInheritedTaxRates,
    showStandardTaxRates,
    showInheritedTaxId
  };
};
